import { supabase } from '@/integrations/supabase/client';

export interface SpamDetectionResult {
  isSpam: boolean;
  confidence: number; // 0-1, higher = more confident it's spam
  reasons: string[];
  riskLevel: 'low' | 'medium' | 'high';
  recommendedAction: 'allow' | 'flag' | 'block';
}

export interface UserBehaviorMetrics {
  accountAge: number; // days
  totalReviews: number;
  recentReviews: number; // last 24 hours
  flaggedReviews: number;
  averageRating: number;
  duplicateCount: number;
}

export class SpamDetectionService {
  // Configurable thresholds
  private static readonly SPAM_PATTERNS = [
    /(.)\1{4,}/gi, // Repeated characters (aaaaa)
    /\b(best|amazing|incredible|fantastic)\b.*\b(best|amazing|incredible|fantastic)\b/gi, // Excessive superlatives
    /(\$\d+|\d+\$|discount|sale|buy now|click here)/gi, // Commercial/promotional content
    /[A-Z]{5,}/g, // Excessive caps
    /(https?:\/\/|www\.|\w+\.(com|net|org))/gi, // URLs
    /(.{20,})\1/gi, // Repeated phrases
  ];

  private static readonly PROFANITY_PATTERNS = [
    // Add profanity patterns as needed - keeping it minimal for example
    /\b(spam|fake|scam|fraud)\b/gi,
  ];

  private static readonly RATE_LIMITS = {
    MAX_REVIEWS_PER_HOUR: 3,
    MAX_REVIEWS_PER_DAY: 10,
    MIN_ACCOUNT_AGE_HOURS: 24,
    MIN_CONTENT_LENGTH: 10,
    MAX_CONTENT_LENGTH: 2000,
    MAX_DUPLICATE_SIMILARITY: 0.8,
  };

  /**
   * Analyze review content for spam patterns
   */
  static analyzeContent(content: string, title?: string): {
    patterns: string[];
    score: number;
  } {
    const fullText = `${title || ''} ${content}`.toLowerCase();
    const patterns: string[] = [];
    let score = 0;

    // Check for spam patterns
    this.SPAM_PATTERNS.forEach((pattern, index) => {
      const matches = fullText.match(pattern);
      if (matches && matches.length > 0) {
        patterns.push(`Spam pattern ${index + 1}: ${matches.length} matches`);
        score += matches.length * 0.2;
      }
    });

    // Check for profanity
    this.PROFANITY_PATTERNS.forEach((pattern) => {
      const matches = fullText.match(pattern);
      if (matches && matches.length > 0) {
        patterns.push('Inappropriate language detected');
        score += matches.length * 0.3;
      }
    });

    // Check content quality indicators
    if (content.length < this.RATE_LIMITS.MIN_CONTENT_LENGTH) {
      patterns.push('Content too short');
      score += 0.3;
    }

    if (content.length > this.RATE_LIMITS.MAX_CONTENT_LENGTH) {
      patterns.push('Content excessively long');
      score += 0.2;
    }

    // Check for lack of punctuation (possible bot content)
    const punctuationCount = (content.match(/[.!?]/g) || []).length;
    const wordCount = content.split(' ').length;
    if (wordCount > 20 && punctuationCount === 0) {
      patterns.push('Lack of proper punctuation');
      score += 0.2;
    }

    // Check for excessive emojis or special characters
    const emojiCount = (content.match(/[\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{1F1E0}-\u{1F1FF}]/gu) || []).length;
    if (emojiCount > 10) {
      patterns.push('Excessive emoji usage');
      score += 0.1;
    }

    return { patterns, score: Math.min(score, 1) };
  }

  /**
   * Get user behavior metrics for spam analysis
   */
  static async getUserBehaviorMetrics(userId: string): Promise<UserBehaviorMetrics> {
    try {
      // Get user account creation date
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
      }

      const accountAge = userData 
        ? Math.floor((Date.now() - new Date(userData.created_at).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      // Get review statistics
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, rating, created_at, content')
        .eq('user_id', userId);

      if (reviewsError) {
        console.error('Error fetching user reviews:', reviewsError);
        return {
          accountAge,
          totalReviews: 0,
          recentReviews: 0,
          flaggedReviews: 0,
          averageRating: 0,
          duplicateCount: 0
        };
      }

      const now = Date.now();
      const dayAgo = now - (24 * 60 * 60 * 1000);

      const recentReviews = reviews.filter(r => 
        new Date(r.created_at).getTime() > dayAgo
      ).length;

      const averageRating = reviews.length > 0 
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : 0;

      // Get flagged reviews count
      const { data: flaggedData, error: flaggedError } = await supabase
        .from('review_flags')
        .select('review_id')
        .in('review_id', reviews.map(r => r.id));

      const flaggedReviews = flaggedError ? 0 : (flaggedData?.length || 0);

      // Check for duplicate content
      const duplicateCount = this.countDuplicateContent(reviews.map(r => r.content));

      return {
        accountAge,
        totalReviews: reviews.length,
        recentReviews,
        flaggedReviews,
        averageRating,
        duplicateCount
      };
    } catch (error) {
      console.error('Error getting user behavior metrics:', error);
      return {
        accountAge: 0,
        totalReviews: 0,
        recentReviews: 0,
        flaggedReviews: 0,
        averageRating: 0,
        duplicateCount: 0
      };
    }
  }

  /**
   * Count duplicate or highly similar content
   */
  private static countDuplicateContent(contents: string[]): number {
    let duplicateCount = 0;
    
    for (let i = 0; i < contents.length; i++) {
      for (let j = i + 1; j < contents.length; j++) {
        const similarity = this.calculateSimilarity(contents[i], contents[j]);
        if (similarity > this.RATE_LIMITS.MAX_DUPLICATE_SIMILARITY) {
          duplicateCount++;
        }
      }
    }
    
    return duplicateCount;
  }

  /**
   * Calculate similarity between two strings using simple algorithm
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.calculateEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculate edit distance between two strings
   */
  private static calculateEditDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * Check rate limiting for user
   */
  static async checkRateLimit(userId: string): Promise<{
    allowed: boolean;
    reason?: string;
    waitTime?: number; // minutes
  }> {
    try {
      const { data: recentReviews, error } = await supabase
        .from('reviews')
        .select('created_at')
        .eq('user_id', userId)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      if (error) {
        console.error('Error checking rate limit:', error);
        return { allowed: true }; // Allow on error to avoid blocking legitimate users
      }

      const now = Date.now();
      const hourAgo = now - (60 * 60 * 1000);
      
      const reviewsInLastHour = recentReviews.filter(r => 
        new Date(r.created_at).getTime() > hourAgo
      ).length;

      const reviewsInLastDay = recentReviews.length;

      // Check hourly limit
      if (reviewsInLastHour >= this.RATE_LIMITS.MAX_REVIEWS_PER_HOUR) {
        const oldestInHour = Math.min(...recentReviews
          .filter(r => new Date(r.created_at).getTime() > hourAgo)
          .map(r => new Date(r.created_at).getTime())
        );
        const waitTime = Math.ceil((oldestInHour + 60 * 60 * 1000 - now) / (60 * 1000));
        
        return {
          allowed: false,
          reason: `You can only submit ${this.RATE_LIMITS.MAX_REVIEWS_PER_HOUR} reviews per hour`,
          waitTime
        };
      }

      // Check daily limit
      if (reviewsInLastDay >= this.RATE_LIMITS.MAX_REVIEWS_PER_DAY) {
        return {
          allowed: false,
          reason: `You can only submit ${this.RATE_LIMITS.MAX_REVIEWS_PER_DAY} reviews per day`,
          waitTime: 60 // Suggest waiting an hour
        };
      }

      return { allowed: true };
    } catch (error) {
      console.error('Error in rate limit check:', error);
      return { allowed: true }; // Allow on error
    }
  }

  /**
   * Main spam detection function
   */
  static async detectSpam(
    content: string,
    title: string | undefined,
    userId: string,
    targetType: 'display' | 'sequence',
    targetId: string
  ): Promise<SpamDetectionResult> {
    const reasons: string[] = [];
    let totalScore = 0;

    try {
      // 1. Content analysis
      const contentAnalysis = this.analyzeContent(content, title);
      if (contentAnalysis.score > 0) {
        reasons.push(...contentAnalysis.patterns);
        totalScore += contentAnalysis.score * 0.4; // 40% weight
      }

      // 2. User behavior analysis
      const userMetrics = await this.getUserBehaviorMetrics(userId);
      
      // New account penalty
      if (userMetrics.accountAge < 1) {
        reasons.push('Very new account (less than 1 day old)');
        totalScore += 0.3;
      } else if (userMetrics.accountAge < 7) {
        reasons.push('New account (less than 1 week old)');
        totalScore += 0.1;
      }

      // High activity penalty
      if (userMetrics.recentReviews >= 5) {
        reasons.push('High recent activity (5+ reviews in 24 hours)');
        totalScore += 0.4;
      } else if (userMetrics.recentReviews >= 3) {
        reasons.push('Elevated recent activity (3+ reviews in 24 hours)');
        totalScore += 0.2;
      }

      // Rating patterns
      if (userMetrics.totalReviews > 5) {
        if (userMetrics.averageRating === 5.0) {
          reasons.push('Only gives 5-star ratings');
          totalScore += 0.2;
        } else if (userMetrics.averageRating === 1.0) {
          reasons.push('Only gives 1-star ratings');
          totalScore += 0.3;
        }
      }

      // Duplicate content penalty
      if (userMetrics.duplicateCount > 0) {
        reasons.push(`Has ${userMetrics.duplicateCount} duplicate/similar reviews`);
        totalScore += userMetrics.duplicateCount * 0.2;
      }

      // High flag ratio
      if (userMetrics.totalReviews > 0 && userMetrics.flaggedReviews > 0) {
        const flagRatio = userMetrics.flaggedReviews / userMetrics.totalReviews;
        if (flagRatio > 0.5) {
          reasons.push('High percentage of flagged reviews');
          totalScore += 0.5;
        } else if (flagRatio > 0.2) {
          reasons.push('Some reviews have been flagged');
          totalScore += 0.2;
        }
      }

      // 3. Rate limiting check
      const rateCheck = await this.checkRateLimit(userId);
      if (!rateCheck.allowed) {
        reasons.push(rateCheck.reason || 'Rate limit exceeded');
        totalScore += 0.6;
      }

      // 4. Check for duplicate review on same target
      const { data: existingReview, error: duplicateError } = await supabase
        .from('reviews')
        .select('id')
        .eq('user_id', userId)
        .eq('target_type', targetType)
        .eq('target_id', targetId)
        .limit(1);

      if (!duplicateError && existingReview && existingReview.length > 0) {
        reasons.push('User already reviewed this item');
        totalScore += 0.8;
      }

      // Determine final assessment
      const confidence = Math.min(totalScore, 1);
      let riskLevel: 'low' | 'medium' | 'high' = 'low';
      let recommendedAction: 'allow' | 'flag' | 'block' = 'allow';

      if (confidence >= 0.7) {
        riskLevel = 'high';
        recommendedAction = 'block';
      } else if (confidence >= 0.4) {
        riskLevel = 'medium';
        recommendedAction = 'flag';
      }

      return {
        isSpam: confidence >= 0.4,
        confidence,
        reasons,
        riskLevel,
        recommendedAction
      };

    } catch (error) {
      console.error('Error in spam detection:', error);
      // On error, be permissive but log the issue
      return {
        isSpam: false,
        confidence: 0,
        reasons: ['Error during spam detection - allowed by default'],
        riskLevel: 'low',
        recommendedAction: 'allow'
      };
    }
  }

  /**
   * Log spam detection result for monitoring
   */
  static async logSpamDetection(
    userId: string,
    targetType: 'display' | 'sequence',
    targetId: string,
    result: SpamDetectionResult,
    action: 'allowed' | 'blocked' | 'flagged'
  ): Promise<void> {
    try {
      await supabase
        .from('spam_detection_logs')
        .insert([{
          user_id: userId,
          target_type: targetType,
          target_id: targetId,
          is_spam: result.isSpam,
          confidence: result.confidence,
          reasons: result.reasons,
          risk_level: result.riskLevel,
          recommended_action: result.recommendedAction,
          actual_action: action,
          created_at: new Date().toISOString()
        }]);
    } catch (error) {
      console.error('Error logging spam detection:', error);
      // Don't throw - logging failure shouldn't block the operation
    }
  }
}

export default SpamDetectionService; 